// "Geplante Nachfragen" — the caller's outgoing scheduled follow-ups, with a
// standalone composer and cancel/delete actions.

import { useEffect, useState } from "preact/hooks";
import { Plus, Send, Trash2, Repeat } from "lucide-preact";
import { Layout } from "../components/Layout";
import { FollowUpComposer } from "../components/FollowUpComposer";
import { PageShell } from "../components/ui/PageShell";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { LoadingSkeleton } from "../components/LoadingSkeleton";
import { usePageHeader } from "../lib/pageHeader";
import { requireLabel } from "../lib/navigation";
import { getFollowUps, updateFollowUp, deleteFollowUp, type FollowUpDto } from "../lib/api";
import { formatDateTimeShort } from "../lib/format";
import { toastError, toastSuccess } from "../lib/toast";

const RECURRENCE_LABEL: Record<FollowUpDto["recurrence"], string> = {
  none: "Einmalig",
  daily: "Täglich",
  weekly: "Wöchentlich",
};

const STATUS_LABEL: Record<FollowUpDto["status"], string> = {
  pending: "Geplant",
  sending: "Wird gesendet",
  sent: "Gesendet",
  cancelled: "Abgebrochen",
};

export function FollowUps(): preact.JSX.Element {
  const [rows, setRows] = useState<FollowUpDto[] | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);

  const load = (): void => {
    void getFollowUps().then(
      (r) => setRows(r),
      () => {
        // War bis hierher ein verschluckter Fehler (kein Toast) — der Nutzer
        // sah nur "Noch keine geplanten Nachfragen" und konnte einen echten
        // Ladefehler nicht von einer wirklich leeren Liste unterscheiden.
        setRows([]);
        toastError("Nachfragen konnten nicht geladen werden.");
      }
    );
  };
  useEffect(load, []);

  usePageHeader({
    // Titel kommt aus der Navigationsquelle, damit er nicht vom Sidebar-Label
    // abweicht. Die Praezisierung („geplante") steckt jetzt im Untertitel —
    // dort gehoert sie hin, ein abweichender Titel waere Drift.
    title: requireLabel("/follow-ups"),
    subtitle: "Geplante Nachfragen zu Aufgaben, die du im Blick behalten willst.",
    actions: (
      <Button icon={Plus} onClick={() => setComposerOpen(true)}>
        Neue Nachfrage
      </Button>
    ),
  });

  const cancel = async (row: FollowUpDto): Promise<void> => {
    try {
      await updateFollowUp(row.id, { status: "cancelled" });
      toastSuccess("Nachfrage abgebrochen");
      load();
    } catch {
      toastError("Abbrechen fehlgeschlagen");
    }
  };

  const remove = async (row: FollowUpDto): Promise<void> => {
    try {
      await deleteFollowUp(row.id);
      load();
    } catch {
      toastError("Löschen fehlgeschlagen");
    }
  };

  return (
    <Layout>
      {/* narrow: reine Lese-/Listenflaeche wie zuvor (max-w-3xl = 768px = narrow). */}
      <PageShell width="narrow">
        {rows === null ? (
          <div class="flex flex-col gap-2">
            <LoadingSkeleton height={56} />
            <LoadingSkeleton height={56} />
            <LoadingSkeleton height={56} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Send}
            title="Noch keine geplanten Nachfragen"
            description="Plane eine Erinnerung an einen Kollegen."
          />
        ) : (
          <ul class="flex flex-col gap-2">
            {rows.map((r) => (
              <li
                key={r.id}
                class="flex items-start justify-between gap-4 rounded-lg border border-[color:var(--color-outline-variant)] p-3"
              >
                <div class="min-w-0">
                  <p class="text-sm font-medium text-[color:var(--color-on-surface)] truncate">
                    {r.subject}
                  </p>
                  <p class="text-xs text-[color:var(--color-on-surface-variant)] truncate">
                    An {r.recipient_name ?? `#${r.recipient_user_id}`} ·{" "}
                    {formatDateTimeShort(r.remind_at)}
                    {r.recurrence !== "none" && (
                      <span class="inline-flex items-center gap-1 ml-1">
                        · <Repeat size={14} aria-hidden="true" /> {RECURRENCE_LABEL[r.recurrence]}
                      </span>
                    )}
                    {" · "}
                    {STATUS_LABEL[r.status]}
                  </p>
                </div>
                <div class="flex shrink-0 items-center gap-1">
                  {r.status === "pending" && (
                    <button
                      type="button"
                      onClick={() => void cancel(r)}
                      class="rounded px-2 py-1 text-xs text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)]"
                    >
                      Abbrechen
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void remove(r)}
                    class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-error)]"
                    aria-label="Nachfrage löschen"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {composerOpen && (
          <FollowUpComposer
            onClose={() => setComposerOpen(false)}
            onCreated={load}
          />
        )}
      </PageShell>
    </Layout>
  );
}
